import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, getFormValues, reduxForm } from 'redux-form';

import type { W_OrganizationCredential_FindOrganizationCredentials_VM_Row } from 'ab-api/web/organizationCredential/findAllForTable';

import type { RootState } from 'af-reducers';

import * as FORMS from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Input from 'af-fields/Input';

import MultiSelectDropdown from 'af-components/Controls/MultiSelectDropdown';
import CustomModal from 'af-components/CustomModal';

import * as OrganizationCredentialActions from 'af-actions/organizationCredential';

import { CreateOrganizationCredentialFM } from './formModel';

interface OwnProps {
	row: Nullable<W_OrganizationCredential_FindOrganizationCredentials_VM_Row>;
	showModal: boolean;
	onConfirm: () => void;
	closeModal: () => void;
}

type Props = OwnProps & InjectedFormProps<CreateOrganizationCredentialFM> & ConnectedProps<typeof connector>;

const CredentialUpsertModal: React.FC<Props> = (props) => {
	const {
		row,
		change,
		showModal,
		closeModal,
		initialize,
		valid,
		handleSubmit,
		createOrganizationCredential,
		updateOrganizationCredential,
		onConfirm,
		organizationData,
		currentFormValues,
	} = props;

	const options = React.useMemo(() => {
		return (organizationData?.companies ?? []).map((_company) => ({ id: _company.id, label: _company.name }));
	}, [organizationData?.companies]);

	React.useEffect(() => {
		if (showModal) {
			initialize(row
				? CreateOrganizationCredentialFM.fromVM(row)
				: new CreateOrganizationCredentialFM()
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row, showModal]); // omitting initialize cause of unwanted behavior

	const submit = React.useCallback(async (form: CreateOrganizationCredentialFM) => {
		if (row) {
			await updateOrganizationCredential(row.id, CreateOrganizationCredentialFM.toRM(form));
		} else {
			await createOrganizationCredential(CreateOrganizationCredentialFM.toRM(form));
		}
		onConfirm();
	}, [createOrganizationCredential, onConfirm, row, updateOrganizationCredential]);

	const onCompaniesChange = React.useCallback((_companies: CreateOrganizationCredentialFM['companies']) => {
		change('companies', _companies);
	}, [change]);

	return (
		<>
			<CustomModal
				closeModal={closeModal}
				modalStyle="info"
				showModal={showModal}
				size="md"
			>
				<CustomModal.Header>
					{`${row ? 'Edit' : 'Add New'} Credential`}
				</CustomModal.Header>
				<CustomModal.Body>
					<Field
						component={Input}
						id="name"
						label="Credential Name *"
						name="name"
						placeholder="Enter Credential Name"
						type="text"
					/>
					<MultiSelectDropdown
						autoOpen={false}
						filterName="Companies"
						label="Companies using this Credential *"
						onChange={onCompaniesChange}
						options={options}
						placeholder="Select companies for this credential"
						selectBtnLabel="Apply"
						selected={currentFormValues?.companies ?? []}
					/>
				</CustomModal.Body>
				<CustomModal.Footer>
					<Button
						label="Close"
						onClick={closeModal}
						style="secondary"
					/>
					<Button
						disabled={!valid}
						label="Save"
						onClick={handleSubmit(submit)}
						style="primary"
					/>
				</CustomModal.Footer>
			</CustomModal>
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { organizationData } = state.user;

	const currentFormValues = getFormValues(FORMS.ORGANIZATION_CREATE_CREDENTIAL)(state) as CreateOrganizationCredentialFM;

	return {
		organizationData,
		currentFormValues,
	};
}

function mapDispatchToProps() {
	return {
		createOrganizationCredential: OrganizationCredentialActions.createOrganizationCredential,
		updateOrganizationCredential: OrganizationCredentialActions.updateOrganizationCredential,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	reduxForm<CreateOrganizationCredentialFM>({
		form: FORMS.ORGANIZATION_CREATE_CREDENTIAL,
		validate: CreateOrganizationCredentialFM.validate,
	}),
	React.memo,
	connector
);

export default enhance(CredentialUpsertModal);
